import * as React from "react";

import { IterUtils } from "../../../../../Common/IterableUtils";
import {
    AbsoluteModelPath,
    createModelPath,
    createRelativeFromTokens,
    emptyModelPath,
    isModelPath,
    ModelPath,
    PathTokens,
    ResolvedModelPath,
} from "../../../../../Common/ModelPath/ModelPath";
import { arrayHasLength, arrayIsNotEmpty, isNotNullOrUndefined, reject } from "../../../../../Common/TypingUtils";
import { ModelHunk } from "../../../Engine/Core/ModelStoring/ModelHunk";
import { findFreeInstanceNumber } from "../../../Helpers/StateAccessors/MultilineHelpers";
import { cutExtraSpaces } from "../../../Helpers/CutExtraSpaces";

import { DataPasteCallback, DatePasteStatus } from "./TableDataPasteContext";

interface ExcelContent {
    mainExcelData: string[][];
    additionalExcelData: Map<number, string[][]>;
}

enum PreviousSliceType {
    WithSingleQuoteFirst,
    WithSingleQuoteLast,
    Multiline,
    Default,
    Empty,
}

type TablePasteResult =
    | { data: ModelHunk; newInstanceCount: number; status: DatePasteStatus.SuccessAll }
    | DatePasteStatus.Failure;

export class ExcelPasteUtils {
    private static readonly isStringInQuotes = (string: string): boolean =>
        string.length > 2 && string.slice(-1) === '"' && string[0] === '"';

    private static readonly isTransferFromIndex = (string: string, index: number): boolean =>
        string[index] === "\t" || string[index] === "\n";

    private static readonly deleteWrappedQuotes = (string: string): string => {
        if (this.isStringInQuotes(string)) {
            return string.slice(1, string.length - 1);
        }
        return string;
    };

    private static readonly isTextHasSingleQuoteFirst = (text: string): boolean => {
        const indexFirstQuote = text.indexOf('"');

        const isFirstQuotesStartCellWrap = indexFirstQuote === 0 || this.isTransferFromIndex(text, indexFirstQuote - 1);

        if (!isFirstQuotesStartCellWrap) {
            return false;
        }

        const quotesCount = this.hhh(text, false);

        return quotesCount % 2 === 1;
    };

    private static readonly hhh = (text: string, end: boolean): number => {
        let quotesCount = 0;
        for (let i = 0; i < text.length; i++) {
            if (!end && text[i] === '"') {
                quotesCount++;
            } else if (end && text[text.length - 1 - i] === '"') {
                quotesCount++;
            } else {
                break;
            }
        }
        return quotesCount;
    };

    private static readonly isTextHasSingleQuoteLast = (text: string): boolean => {
        const indexLastQuote = text.lastIndexOf('"');
        const isLastQuotesEndCellWrap =
            indexLastQuote === text.length - 1 || this.isTransferFromIndex(text, indexLastQuote + 1);

        if (!isLastQuotesEndCellWrap) {
            return false;
        }

        const quotesCount = this.hhh(text, true);
        return quotesCount % 2 === 1;
    };

    public static readonly splitExcelContent = (excelContent: string): string[][] => {
        const splitByTabs = excelContent.split("\t");
        const rows: string[][] = [];
        let currentRow: string[] = [];
        for (const maybeColumn of splitByTabs) {
            if (maybeColumn.includes("\n")) {
                const splitByLineEnd = maybeColumn.split(/\r\n|\n\r|\n|\r/g);
                let currentElement = [];
                let previous: PreviousSliceType = PreviousSliceType.Empty;
                for (const text of splitByLineEnd) {
                    if (previous === PreviousSliceType.WithSingleQuoteLast || previous === PreviousSliceType.Default) {
                        rows.push(currentRow);
                        currentRow = [];
                    }
                    if (!this.isTextHasSingleQuoteFirst(text) && !this.isTextHasSingleQuoteLast(text)) {
                        if (previous === PreviousSliceType.Multiline) {
                            currentElement.push(text);
                        } else {
                            currentRow.push(text);
                            previous = PreviousSliceType.Default;
                        }
                    } else if (this.isTextHasSingleQuoteFirst(text) && this.isTextHasSingleQuoteLast(text)) {
                        const cellText = this.deleteWrappedQuotes(text.replaceAll('""', '"'));
                        currentRow.push(cellText);
                        previous = PreviousSliceType.WithSingleQuoteLast;
                    } else if (this.isTextHasSingleQuoteFirst(text)) {
                        currentElement.push(text);
                        previous = PreviousSliceType.Multiline;
                    } else if (this.isTextHasSingleQuoteLast(text)) {
                        if (previous === PreviousSliceType.Multiline) {
                            currentElement.push(text);
                            const cellText = this.deleteWrappedQuotes(currentElement.join("\n").replaceAll('""', '"'));
                            currentRow.push(cellText);
                            currentElement = [];
                        } else {
                            currentRow.push(text);
                        }
                        previous = PreviousSliceType.WithSingleQuoteLast;
                    }
                }

                if (arrayHasLength(currentElement, 2)) {
                    currentRow.push(currentElement[0]);
                    rows.push(currentRow);
                    currentRow = [currentElement[1]];
                }
            } else {
                currentRow.push(maybeColumn);
            }
        }
        rows.push(currentRow);
        return rows;
    };

    public static readonly removeEmptyRowsAndColumns = (table: string[][]): string[][] => {
        const emptyRowsCount = ExcelPasteUtils.countEmptyRows(table);
        const emptyColumnsCount = ExcelPasteUtils.countEmptyColumns(table);

        table.splice(table.length - emptyRowsCount);
        table.forEach(row => row.splice(row.length - emptyColumnsCount));

        return table;
    };

    public static readonly countEmptyRows = (table: string[][]): number => {
        for (let rowIndex = 0; rowIndex < table.length; rowIndex++) {
            if (table[table.length - rowIndex - 1]?.some(element => element !== "")) {
                return rowIndex;
            }
        }
        return table.length - 1;
    };

    public static readonly countEmptyColumns = (table: string[][]): number => {
        if (!arrayIsNotEmpty(table) || !arrayIsNotEmpty(table[0])) {
            return 0;
        }
        let count = table[0].length - 1;
        for (const row of table) {
            for (let columnIndex = 0; columnIndex < row.length; columnIndex++) {
                if (row[row.length - columnIndex - 1] !== "") {
                    if (columnIndex === 0) {
                        return 0;
                    }
                    if (columnIndex < count) {
                        count = columnIndex;
                    }
                }
            }
        }
        return count;
    };

    public static parseClipboardContent = (clipboardContent: DataTransfer): string[][] => {
        const clipboardText = clipboardContent.getData("text");
        return this.splitExcelContent(clipboardText);
    };

    public static WrapHandlePaste =
        (
            onPaste: DataPasteCallback,
            bindingPath: AbsoluteModelPath,
            funcWithPreventDefault?: () => void
        ): ((event: React.ClipboardEvent<unknown>) => DatePasteStatus) =>
        event => {
            const result = onPaste(event.clipboardData, { bindingPath: bindingPath });
            if (result != DatePasteStatus.Failure) {
                event.preventDefault();
                if (funcWithPreventDefault != undefined) {
                    funcWithPreventDefault();
                }
            }
            return result;
        };

    public static findColumnStartIndex = (
        currentBindingPath: AbsoluteModelPath,
        columnsStructure: AbsoluteModelPath[],
        columnStartIndex?: number
    ): number => {
        const reallyColumnStartIndex =
            columnStartIndex ?? ExcelPasteUtils.findColumnIndexByPath(columnsStructure, currentBindingPath);
        return reallyColumnStartIndex;
    };

    public static findRowStartIndex = (rowStartName: string, instances: readonly string[]): number => {
        const rowStartIndex = instances.indexOf(rowStartName);
        if (rowStartIndex === -1) {
            return 0;
        }
        return rowStartIndex;
    };

    private static readonly getOnlyNotContainingMultiFieldIndexes = (columnsNames: ModelPath[]): number[] => {
        const columnsContainingMultiField = columnsNames
            .map((path, index) => ({ value: path, index: index }))
            .filter(tuple => tuple.value.toLegacyPath().includes("*")); // test!!@!!
        const onlyContainingMultiFieldIndexes = columnsContainingMultiField.map(({ index }) => index);
        const allColumnNamesIndexes = columnsNames.map((_, index) => index);
        const onlyNotContainingMultiFieldIndexes = allColumnNamesIndexes.filter(
            index => !onlyContainingMultiFieldIndexes.includes(index)
        );
        return onlyNotContainingMultiFieldIndexes;
    };

    private static readonly allIndexesEmptyInArray = (
        array: string[],
        indexes: number[],
        indexOffset: number = 0
    ): boolean =>
        indexes.every(
            index => array.length <= index - indexOffset || array[index - indexOffset] === "" || index - indexOffset < 0
        );

    public static splitExcelData = (
        excData: string[][],
        columnsNames: ModelPath[],
        columnStartIndex: number
    ): ExcelContent => {
        const onlyNotContainingMultiFieldIndexes = ExcelPasteUtils.getOnlyNotContainingMultiFieldIndexes(columnsNames);

        const allMultiFieldsTables = new Map<number, string[][]>();
        let currentMultiFieldTable = [];
        const mainExcelData = [];
        let rowForAppendMultiFieldTableIndex = -1;
        let squashRowCount = 0;

        for (let i = 0; i < excData.length; i++) {
            const row = excData[i];
            if (row === undefined) {
                break;
            }
            const isMultiFieldTableRow = ExcelPasteUtils.allIndexesEmptyInArray(
                row,
                onlyNotContainingMultiFieldIndexes,
                columnStartIndex
            );
            if (isMultiFieldTableRow && onlyNotContainingMultiFieldIndexes.includes(columnStartIndex)) {
                if (currentMultiFieldTable.length === 0) {
                    rowForAppendMultiFieldTableIndex = i - 1 - squashRowCount;
                }
                currentMultiFieldTable.push(row);
            } else {
                if (currentMultiFieldTable.length !== 0) {
                    allMultiFieldsTables.set(rowForAppendMultiFieldTableIndex, currentMultiFieldTable);
                    rowForAppendMultiFieldTableIndex = -1;
                    squashRowCount += currentMultiFieldTable.length;
                    currentMultiFieldTable = [];
                }
                mainExcelData.push(row);
            }
        }

        if (currentMultiFieldTable.length !== 0) {
            allMultiFieldsTables.set(rowForAppendMultiFieldTableIndex, currentMultiFieldTable);
        }

        return { mainExcelData: mainExcelData, additionalExcelData: allMultiFieldsTables };
    };

    private static IsSingleCell(excelData: string[][]): boolean {
        return arrayHasLength(excelData, 1) && arrayHasLength(excelData[0], 1);
    }

    public static pasteTableData = (
        excelData: string[][],
        instances: string[] | readonly string[],
        columnNames: Array<ModelPath | undefined>,
        rowStartIndex: number,
        columnStartIndex: number,
        ignoreEmptyData: boolean = false,
        multifieldModeIndexFinder: boolean = false
    ): TablePasteResult => {
        if (arrayHasLength(excelData, 0) || this.IsSingleCell(excelData) || excelData.length === 0) {
            return DatePasteStatus.Failure;
        }
        const rowsCount = instances.length;
        const withoutEmptyEnding = this.removeEmptyRowsAndColumns(excelData);

        if (this.IsSingleCell(withoutEmptyEnding)) {
            return DatePasteStatus.Failure;
        }

        const data = ExcelPasteUtils.fillInstances(
            withoutEmptyEnding,
            instances,
            rowStartIndex,
            columnStartIndex,
            columnNames,
            ignoreEmptyData,
            multifieldModeIndexFinder
        );

        const newInstanceCount = Math.max(rowStartIndex + excelData.length - rowsCount, 0);
        return { data: data, newInstanceCount: newInstanceCount, status: DatePasteStatus.SuccessAll };
    };

    public static getPathsToMultiFieldByColumn(
        modelHunk: ModelHunk,
        columnsPrefixes: Array<ResolvedModelPath | undefined>,
        multiFieldColumns: Array<ResolvedModelPath | undefined>,
        multilineRowIndex: number,
        rowStartIndex: number,
        rowsIndexes: readonly string[]
    ): ResolvedModelPath[] {
        return Array.from(modelHunk.iterateEntries()).map(([modelPath, _]): ResolvedModelPath => {
            const pathParts = modelPath.getPathPartsAsArray();
            const lastPart = arrayHasLength(pathParts, 1) ? "" : (IterUtils.last(pathParts)?.toString() ?? "");
            const prefix = columnsPrefixes[multiFieldColumns.findIndex(oo => oo?.toLegacyPath().includes(lastPart))];

            const resultModelPath = createRelativeFromTokens([
                // если такой строчки не существует, то надо добавить новую, но то что  multilineRowIndex + rowStartIndex совпадет с новой не гарантированно
                rowsIndexes[multilineRowIndex + rowStartIndex] ?? (multilineRowIndex + rowStartIndex).toString(),
                ...(prefix?.getPathParts() ?? ""),
            ]);

            return resultModelPath;
        });
    }

    public static splitColumnNames(
        columnNames: ModelPath[]
    ): [Array<ResolvedModelPath | undefined>, Array<ResolvedModelPath | undefined>] {
        const columnNamesFirstAndLastPair = columnNames.map(bp => {
            const parts = Array.from(bp.splitByMultiplicity());
            if (bp.isResolved() || !arrayIsNotEmpty(parts)) {
                return { first: undefined, last: undefined };
            }

            const first = IterUtils.first(parts);
            const last = IterUtils.last(parts);
            const ff = isModelPath(first) ? first : emptyModelPath();
            const ll = isModelPath(last) ? last : emptyModelPath();
            return { first: ff, last: ll };
        });
        const lastColumnParts = columnNamesFirstAndLastPair.map(o => o.last);
        const firstColumnParts = columnNamesFirstAndLastPair.map(o => o.first);

        return [firstColumnParts, lastColumnParts];
    }

    private static readonly copyReadonlyRows = (rows: readonly string[]): string[] => [...rows];

    private static readonly fillInstances = (
        excelData: string[][],
        rows: readonly string[],
        rowStartIndex: number,
        columnStartIndex: number,
        columnNames: Array<ModelPath | undefined>,
        ignoreEmptyData: boolean,
        multifieldModeIndexFinder: boolean = false
    ): ModelHunk => {
        const result: ModelHunk = new ModelHunk();
        const newRows = this.copyReadonlyRows(rows);

        for (let i = 0; i < excelData.length; i++) {
            const instanceIndex = this.findInstanceIndex(rows, i + rowStartIndex, newRows, multifieldModeIndexFinder);
            const modelHunk = ExcelPasteUtils.fillInstance(
                instanceIndex,
                excelData[i] ?? reject(),
                columnStartIndex,
                columnNames,
                ignoreEmptyData
            );
            for (const [modelPath, viewModel] of modelHunk.iterateEntries()) {
                result.set(modelPath, viewModel);
            }
        }
        return result;
    };

    public static findInstanceIndex(
        rows: readonly string[],
        possibleIndex: number,
        newRows: string[],
        multifieldModeIndexFinder: boolean = false
    ): string {
        if (rows.length > possibleIndex && isNotNullOrUndefined(rows[possibleIndex])) {
            return rows[possibleIndex] ?? reject("rows[possibleIndex] is undefined");
        } else {
            const instanceIndex = multifieldModeIndexFinder
                ? possibleIndex.toString()
                : findFreeInstanceNumber(newRows);
            newRows.push(instanceIndex);
            return instanceIndex;
        }
    }

    private static readonly fillInstance = (
        instanceIndex: string,
        rowData: string[],
        columnStartIndex: number,
        columns: Array<ModelPath | undefined>,
        ignoreEmptyData: boolean
    ) => {
        const result: ModelHunk = new ModelHunk();
        const rightBorder = Math.min(rowData.length, columns.length - columnStartIndex);
        for (let i = 0; i < rightBorder; i++) {
            const currentColumn = columns[columnStartIndex + i];
            if (currentColumn === undefined || (ignoreEmptyData && rowData[i] === "")) {
                continue;
            }
            const path = createModelPath(instanceIndex.toString(), false).joinWith(currentColumn);
            const formattedText = cutExtraSpaces(rowData[i]);
            result.set(path, {
                value: formattedText,
            });
        }

        return result;
    };

    private static readonly findColumnIndexByPath = (
        columnsStructure: AbsoluteModelPath[],
        bindingPath: AbsoluteModelPath
    ): number => {
        if (columnsStructure == undefined) {
            return 0;
        }

        const bindingPathParts = bindingPath.getPathPartsAsArray();

        const index = columnsStructure.findIndex(columnPath => {
            const columnPathParts = columnPath.getPathPartsAsArray();
            if (columnPathParts.length !== bindingPathParts.length) {
                return false;
            }
            for (let i = 0; i < columnPathParts.length; i++) {
                if (!PathTokens.isMultiToken(columnPathParts[i]) && bindingPathParts[i] !== columnPathParts[i]) {
                    return false;
                }
            }
            return true;
        });

        if (index === -1) {
            return 0;
        }
        return index;
    };
}
